import React from 'react';
import { Info } from 'lucide-react';
import { privacyLaws, PrivacyLaw } from '../../data/privacyLaws';

interface PrivacyLawSelectorProps {
  selectedLaws: string[];
  onLawsChange: (laws: string[]) => void;
}

export default function PrivacyLawSelector({ selectedLaws, onLawsChange }: PrivacyLawSelectorProps) {
  const categories = {
    global: 'Global Privacy Laws',
    usFederal: 'US Federal Laws',
    usState: 'US State Laws'
  };

  const handleLawChange = (lawId: string, checked: boolean) => {
    if (checked) {
      onLawsChange([...selectedLaws, lawId]);
    } else {
      onLawsChange(selectedLaws.filter(id => id !== lawId));
    }
  };

  const renderLawCategory = (category: keyof typeof categories, laws: PrivacyLaw[]) => (
    <div key={category} className="space-y-4">
      <h3 className="font-medium text-gray-900">{categories[category]}</h3>
      <div className="grid md:grid-cols-2 gap-4">
        {laws.map((law) => (
          <div key={law.id} className="relative flex items-start">
            <div className="flex items-center h-6">
              <input
                id={law.id}
                type="checkbox"
                className="rounded border-gray-300 text-brand-primary focus:ring-brand-primary"
                checked={selectedLaws.includes(law.id)}
                onChange={(e) => handleLawChange(law.id, e.target.checked)}
              />
            </div>
            <div className="ml-3 group">
              <label htmlFor={law.id} className="text-sm font-medium text-gray-700">
                {law.name}
              </label>
              <div className="hidden group-hover:block absolute left-0 top-6 z-10 w-72 p-4 bg-white rounded-lg shadow-lg border border-gray-100">
                <p className="text-sm text-gray-600">{law.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold">Select Privacy Laws</h2>
        <div className="flex items-center text-sm text-gray-500">
          <Info className="w-4 h-4 mr-1" />
          <span>Hover for details</span>
        </div>
      </div>

      <div className="space-y-8">
        {renderLawCategory('global', privacyLaws.filter(law => law.category === 'global'))}
        {renderLawCategory('usFederal', privacyLaws.filter(law => law.category === 'usFederal'))}
        {renderLawCategory('usState', privacyLaws.filter(law => law.category === 'usState'))}
      </div>
    </div>
  );
}